import { Box, Icon, Text, Wrap, WrapItem } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { FiFileText, FiShoppingCart } from "react-icons/fi";
import type { EntityHighlight } from "./agentTypes";
import { inferEntityHighlightKind } from "./entityHighlights";

type LookupEntity = (id: string) => Record<string, unknown> | undefined;

const ICON_BY_KIND: Record<"contract" | "event", IconType> = {
  contract: FiFileText,
  event: FiShoppingCart,
};

// Small clickable cards for the entities a turn's tool calls surfaced (a created/updated contract, a
// sourcing event/project/need the user just asked about) — lets the user jump straight to that page
// instead of asking the assistant to navigate for them. The icon is a best-effort contract/event sniff
// (see inferEntityHighlightKind); anything else — project, need — renders label-only.
export const EntityHighlightCards = ({
  entities,
  lookupEntity,
  onOpen,
}: {
  entities: EntityHighlight[];
  lookupEntity: LookupEntity;
  onOpen: (id: string) => void;
}) => (
  <Wrap spacing="2">
    {entities.map((entity) => {
      const kind = inferEntityHighlightKind(lookupEntity(entity.id));
      const icon = kind ? ICON_BY_KIND[kind] : undefined;
      return (
        <WrapItem key={entity.id}>
          <Box
            as="button"
            type="button"
            onClick={() => onOpen(entity.id)}
            display="flex"
            alignItems="center"
            gap="2"
            px="3"
            py="2"
            borderWidth="1px"
            borderColor="smBorder"
            borderRadius="lg"
            bg="smBackground"
            textAlign="left"
            _hover={{ borderColor: "smPrimary", bg: "smPrimarySofter" }}
          >
            {icon && <Icon as={icon} boxSize="4" color="smPrimary" flexShrink={0} />}
            <Box minW="0">
              <Text fontSize="sm" fontWeight="semibold" color="smText" noOfLines={1}>
                {entity.label}
              </Text>
              {entity.sublabel && (
                <Text fontSize="xs" color="smTextDisabled" noOfLines={1}>
                  {entity.sublabel}
                </Text>
              )}
            </Box>
          </Box>
        </WrapItem>
      );
    })}
  </Wrap>
);
